import type { SandboxProfile } from './types';

/** What a url-kind zone or preview should actually put in its iframe. */
export interface UrlFrameSource {
  src: string;
  sandbox: string;
  referrerPolicy: 'no-referrer' | 'strict-origin-when-cross-origin';
}

const YOUTUBE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'music.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
]);

// IDs are 11 chars today, but Google has never committed to that; accept a
// sane range rather than hard-coding the current width.
const VIDEO_ID_RE = /^[A-Za-z0-9_-]{6,20}$/;

/**
 * Extract the video id from any of the YouTube URL shapes users paste:
 * watch?v=, youtu.be/, /shorts/, /live/ and an already-embed /embed/ URL.
 * Returns null for anything that is not recognizably a single YouTube video.
 */
export function youtubeVideoId(raw: string): string | null {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  const host = url.hostname.toLowerCase();
  const segments = url.pathname.split('/').filter(Boolean);
  if (host === 'youtu.be') {
    const id = segments[0] ?? '';
    return VIDEO_ID_RE.test(id) ? id : null;
  }
  if (!YOUTUBE_HOSTS.has(host)) return null;
  if (segments[0] === 'watch') {
    const id = url.searchParams.get('v') ?? '';
    return VIDEO_ID_RE.test(id) ? id : null;
  }
  if (segments[0] === 'shorts' || segments[0] === 'live' || segments[0] === 'embed') {
    const id = segments[1] ?? '';
    return VIDEO_ID_RE.test(id) ? id : null;
  }
  return null;
}

/**
 * Poster image for a YouTube video URL, or null when the URL is not a
 * recognizable YouTube video. hqdefault (480×360) exists for every video,
 * unlike maxresdefault which 404s on older/low-res uploads.
 */
export function youtubeThumbnailUrl(raw: string): string | null {
  const id = youtubeVideoId(raw);
  return id === null ? null : `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
}

/** `t=90`, `t=90s` or `start=90` on the pasted URL, as whole seconds. */
function startSeconds(raw: string): number | null {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  const t = url.searchParams.get('start') ?? url.searchParams.get('t');
  if (!t) return null;
  const match = /^(\d+)s?$/.exec(t);
  return match ? Number(match[1]) : null;
}

function youtubeEmbedUrl(id: string, raw: string): string {
  const params = new URLSearchParams({
    autoplay: '1',
    mute: '1',
    loop: '1',
    // loop=1 only works when the video is its own single-entry playlist.
    playlist: id,
    controls: '0',
    rel: '0',
    playsinline: '1',
  });
  const start = startSeconds(raw);
  if (start !== null) params.set('start', String(start));
  return `https://www.youtube-nocookie.com/embed/${id}?${params.toString()}`;
}

/**
 * Resolve what to frame for a url asset. YouTube page URLs refuse framing
 * outright (X-Frame-Options / frame-ancestors), so they are rewritten to the
 * embed player at render time — the stored asset URL stays as entered. The
 * embed player additionally errors out under a sandbox's opaque origin, so
 * recognized YouTube videos always get allow-same-origin regardless of the
 * asset's sandbox profile; framing youtube.com that way is no weaker than the
 * interactive profile already on offer.
 *
 * The embed player also refuses to run without referrer information (error
 * 153: "Fehler bei der Konfiguration des Videoplayers") — YouTube needs the
 * embedding origin to enforce per-site embed restrictions. So YouTube frames
 * send the origin (and only the origin: strict-origin-when-cross-origin never
 * leaks the page path cross-origin), while arbitrary URLs keep no-referrer.
 */
export function urlFrameSource(raw: string, profile: SandboxProfile): UrlFrameSource {
  const id = youtubeVideoId(raw);
  if (id !== null) {
    return {
      src: youtubeEmbedUrl(id, raw),
      sandbox: 'allow-scripts allow-same-origin',
      referrerPolicy: 'strict-origin-when-cross-origin',
    };
  }
  return {
    src: raw,
    sandbox:
      profile === 'interactive' ? 'allow-scripts allow-same-origin allow-forms' : 'allow-scripts',
    referrerPolicy: 'no-referrer',
  };
}
